import { PageController } from '@client/components/_core/layout/PageController';
import { PublicNoIndex } from '@client/components/_shared/public/PublicNoIndex';
import { InlineQueryError } from '@client/components/_ui/feedback/InlineQueryError';
import { Loading } from '@client/components/_ui/feedback/Loading';
import { DriverClaimView } from '@client/components/claims/driver/DriverClaimView';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';

export const Route = createFileRoute('/public/lodgement-summary/$accessKey')({ component: DriverClaimPage });

function DriverClaimPage() {
    const { accessKey } = Route.useParams();
    const { trpc } = useTrpc();
    const query = trpc.claim.getClaimByAccessKey.useQuery(
        { accessKey },
        {
            trpc: { context: { captcha: true, captchaSingleUse: true } },
            meta: { errorSurface: 'inline' },
            staleTime: 0,
            gcTime: 0,
            retry: false,
            refetchOnMount: 'always',
            refetchOnWindowFocus: true,
        },
    );
    const errorCode = query.error?.data?.code;
    const definitiveDenial = errorCode === 'NOT_FOUND' || errorCode === 'BAD_REQUEST' || errorCode === 'FORBIDDEN';
    const [accessDenial, setAccessDenial] = useState({ accessKey, denied: false });
    // A later transport/CAPTCHA error must not reveal data retained before a denial.
    // Reset only on successful revalidation or when navigating to a different key.
    const unavailable =
        definitiveDenial || (accessDenial.accessKey === accessKey && accessDenial.denied && !query.isSuccess);
    if (accessDenial.accessKey !== accessKey || accessDenial.denied !== unavailable) {
        setAccessDenial({ accessKey, denied: unavailable });
    }
    // This public procedure uses UNAUTHORIZED for CAPTCHA rejection. Retrying
    // mints a fresh single-use challenge; it does not require a login.
    const retryable = unavailable ? false : errorCode === 'UNAUTHORIZED' ? true : undefined;
    return (
        <PageController route={Route}>
            <PublicNoIndex />
            <main className="mx-auto w-full max-w-lg py-2">
                {query.isLoading ? (
                    <div className="py-12">
                        <Loading description="Loading your claim" />
                    </div>
                ) : query.data && !unavailable ? (
                    <>
                        {query.error && (
                            <InlineQueryError
                                compact
                                className="mb-3"
                                error={query.error}
                                title="Unable to refresh your claim"
                                description="Showing the last loaded information. Please try again for the latest update."
                                retryable={retryable}
                                onRetry={() => void query.refetch()}
                                retrying={query.isFetching}
                            />
                        )}
                        <DriverClaimView claim={query.data} />
                    </>
                ) : query.error ? (
                    <InlineQueryError
                        error={query.error}
                        title={unavailable ? 'Claim view unavailable' : 'Unable to load your claim'}
                        description={
                            unavailable
                                ? 'Please check the link in your claim message or contact your claims handler.'
                                : 'Please try again to load the latest claim information.'
                        }
                        onRetry={() => void query.refetch()}
                        retryable={retryable}
                        retrying={query.isFetching}
                    />
                ) : null}
            </main>
        </PageController>
    );
}
